import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../Security/firebase-config";
import { FUNCTION_NAMES } from "../utils/functionNames";
import { useAuth } from "./AuthContext";
import { useTeam } from "./TeamContext";
import { resolveUserPersonalEntitlements } from "../utils/entitlements";

const AccessContext = createContext(null);
export const useAccess = () => useContext(AccessContext);
const EMPTY = {
  activeTeamId: null, activeTeamEntitlements: null, activeTeamSubscription: null,
  allTeamSubscriptions: [], pendingJoinAccess: null,
  personalEntitlements: resolveUserPersonalEntitlements([]),
};

export const AccessProvider = ({ children }) => {
  const { userData, loading: authLoading, error: authError } = useAuth();
  const { teamId } = useTeam();
  const uid = userData?.uid;
  const identity = JSON.stringify([uid || null, teamId || null]);
  const generation = useRef(0);
  const [state, setState] = useState({ identity: null, loading: true, error: null, ...EMPTY });

  const refreshAccess = useCallback(async () => {
    const request = ++generation.current;
    if (!uid || authLoading || authError) return;
    setState({ identity, loading: true, error: null, ...EMPTY });
    try {
      const callable = httpsCallable(functions, FUNCTION_NAMES.getResolvedAccess);
      const result = await callable({});
      if (request !== generation.current) return;
      const data = result?.data;
      if (!data || !Object.prototype.hasOwnProperty.call(data, "activeTeamId")) {
        throw new Error("Access response was incomplete. Please try again.");
      }
      const allTeamSubscriptions = Array.isArray(data.allTeamSubscriptions) ? data.allTeamSubscriptions : [];
      setState({
        identity, loading: false, error: null,
        activeTeamId: data.activeTeamId || null,
        activeTeamSubscription: data.activeTeamSubscription || null,
        activeTeamEntitlements: data.activeTeamEntitlements || null,
        pendingJoinAccess: data.pendingJoinAccess || null,
        allTeamSubscriptions,
        personalEntitlements: data.personalEntitlements || resolveUserPersonalEntitlements(allTeamSubscriptions),
      });
    } catch (error) {
      if (request === generation.current) setState({ identity, loading: false, error, ...EMPTY });
    }
  }, [uid, identity, authLoading, authError]);

  useEffect(() => {
    refreshAccess();
    return () => { generation.current += 1; };
  }, [refreshAccess]);

  // Mask stale state during render, before the next effect starts its request.
  const current = !uid || authError
    ? { ...EMPTY, loading: authLoading, error: null }
    : state.identity === identity ? state : { ...EMPTY, loading: true, error: null };
  const teamError = !current.loading && !current.error && uid && current.activeTeamId !== (teamId || null)
    ? new Error("Your active team changed before access was confirmed. Please try again.") : null;

  return <AccessContext.Provider value={{
    ...current,
    activeTeamSubscription: teamError ? null : current.activeTeamSubscription,
    activeTeamEntitlements: teamError ? null : current.activeTeamEntitlements,
    teamError, refreshAccess,
  }}>{children}</AccessContext.Provider>;
};
